import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { Country, State, City } from 'country-state-city';
import {
  CheckCircle, User, Mail, Lock, ChevronRight, ChevronLeft,
  Hash, AlertCircle, Loader2, GitBranch, MapPin,
  Eye, EyeOff, Calendar, Shield, X, CreditCard, Landmark
} from 'lucide-react';

// Reusing the Simple Modal Component - Light Theme Adapted
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-gray-100 rounded-2xl w-full max-w-lg p-6 shadow-2xl animate-scaleIn">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="text-gray-600 text-sm max-h-[60vh] overflow-y-auto custom-scrollbar space-y-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();

  // Step Management
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);

  // Auto-scroll on step change
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Sponsor State
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [sponsorMessage, setSponsorMessage] = useState({ text: '', type: '' });
  const [sponsorIsFull, setSponsorIsFull] = useState(false);
  const [extremePositions, setExtremePositions] = useState(null);

  // Validation Messages
  const [mobileMessage, setMobileMessage] = useState({ text: '', type: '' });
  const [panMessage, setPanMessage] = useState({ text: '', type: '' });
  const [bankAccountMessage, setBankAccountMessage] = useState({ text: '', type: '' });

  // Password Visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Location States
  const [countries] = useState(Country.getAllCountries());
  const [states, setStates] = useState(State.getStatesOfCountry('IN'));
  const [cities, setCities] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState('IN');
  const [selectedStateCode, setSelectedStateCode] = useState('');

  // Validation Patterns
  const PATTERNS = {
    mobile: /^[6-9][0-9]{9}$/
  };

  // Form Data (Exactly matches Register.js structure)
  const [formData, setFormData] = useState({
    // Step 1: Sponsor Details
    sponsorId: '',
    sponsorName: '',
    placement: 'left',

    // Step 2: Personal Details
    fullName: '',
    gender: 'male',
    maritalStatus: 'single',
    nomineeName: '',
    nomineeRelation: '',
    email: '',
    mobile: '',
    dateOfBirth: '',
    panNumber: '',
    bankAccountNumber: '',

    // Step 3: Address Details
    country: 'India',
    state: '',
    city: '',
    pinCode: '',
    fullAddress: '',

    // Step 4: Account Security
    password: '',
    confirmPassword: '',
    transactionPassword: '',

    // Declaration
    agreeTerms: false
  });

  // Steps Configuration
  const steps = [
    { number: 1, title: 'Sponsor', icon: GitBranch },
    { number: 2, title: 'Personal', icon: User },
    { number: 3, title: 'Address', icon: MapPin },
    { number: 4, title: 'Security', icon: Shield }
  ];

  // Handle Form Changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;

    // Mobile - numbers only, max 10
    if (name === 'mobile') {
      finalValue = value.replace(/\D/g, '').slice(0, 10);
      if (mobileMessage.text) setMobileMessage({ text: '', type: '' });
    }

    // Pin code - numbers only, max 6
    if (name === 'pinCode') {
      finalValue = value.replace(/\D/g, '').slice(0, 6);
    }

    // Transaction Password - numbers only, max 4
    if (name === 'transactionPassword') {
      finalValue = value.replace(/\D/g, '').slice(0, 4);
    }

    // PAN Number - uppercase, max 10 alphanumeric
    if (name === 'panNumber') {
      finalValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    }

    // Bank Account Number - digits only, max 18
    if (name === 'bankAccountNumber') {
      finalValue = value.replace(/\D/g, '').slice(0, 18);
    }

    // Full Name - All caps
    if (name === 'fullName') {
      finalValue = value.toUpperCase();
    }

    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  // Verify Sponsor (Exactly like Register.js)
  const verifySponsor = async (sponsorCode) => {
    if (!sponsorCode) {
      setSponsorMessage({ text: '', type: '' });
      setSponsorIsFull(false);
      setExtremePositions(null);
      setFormData(prev => ({ ...prev, sponsorName: '' }));
      return;
    }

    setSponsorLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user/check_sponsor_positions.php?sponsor_id=${sponsorCode}`);
      const data = await response.json();

      if (data.success) {
        setFormData(prev => ({ ...prev, sponsorName: data?.sponsor?.name || '' }));
        const branchInfo = {
          left: data?.left_branch,
          right: data?.right_branch
        };
        setExtremePositions(branchInfo);
        setSponsorIsFull(!!data?.is_full);

        if (data.is_full) {
          setSponsorMessage({
            text: `⚠️ Sponsor is FULL! You must choose an Extreme position.`,
            type: 'warning'
          });
          setFormData(prev => ({ ...prev, placement: 'extreme_left' }));
        } else {
          if (data?.available_positions && data.available_positions.length > 0) {
            setFormData(prev => ({ ...prev, placement: data.available_positions[0] }));
          }

          if (data?.left_branch?.is_available && data?.right_branch?.is_available) {
            setSponsorMessage({
              text: `✓ Sponsor: ${data?.sponsor?.name || 'User'}. Both branches available.`,
              type: 'success'
            });
          } else {
            const avail = data?.left_branch?.is_available ? 'LEFT' : 'RIGHT';
            setSponsorMessage({
              text: `✓ Sponsor: ${data?.sponsor?.name || 'User'}. Direct ${avail} available. Opposite is FULL.`,
              type: 'success'
            });
          }
        }
      } else {
        setFormData(prev => ({ ...prev, sponsorName: '' }));
        setSponsorIsFull(false);
        setExtremePositions(null);
        setSponsorMessage({
          text: '❌ Invalid Sponsor ID. ' + (data.message || 'Sponsor not found.'),
          type: 'error'
        });
      }
    } catch (e) {
      console.error("Sponsor check failed", e);
      setSponsorMessage({ text: '❌ Failed to verify sponsor.', type: 'error' });
    } finally {
      setSponsorLoading(false);
    }
  };

  // Location Handlers
  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    const countryName = e.target.options[e.target.selectedIndex].text;
    setSelectedCountryCode(countryCode);
    setFormData(prev => ({ ...prev, country: countryName, state: '', city: '' }));
    setStates(State.getStatesOfCountry(countryCode));
    setCities([]);
    setSelectedStateCode('');
  };

  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    const stateName = e.target.options[e.target.selectedIndex].text;
    setSelectedStateCode(stateCode);
    setFormData(prev => ({ ...prev, state: stateName, city: '' }));
    setCities(City.getCitiesOfState(selectedCountryCode, stateCode));
  };

  // Validation
  const handleMobileBlur = () => {
    if (!formData.mobile) return;
    if (!PATTERNS.mobile.test(formData.mobile)) {
      setMobileMessage({ text: '⚠️ Invalid mobile. Must be 10 digits starting with 6-9.', type: 'error' });
    } else {
      setMobileMessage({ text: '✓ Valid mobile number', type: 'success' });
    }
  };

  // PAN Duplicate Check
  const handlePanBlur = async () => {
    if (!formData.panNumber) { setPanMessage({ text: '', type: '' }); return; }
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    if (!panRegex.test(formData.panNumber)) {
      setPanMessage({ text: '⚠️ Invalid PAN format (e.g. ABCDE1234F)', type: 'error' });
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/auth/check_kyc.php?type=pan&value=${formData.panNumber}`);
      const data = await response.json();
      if (data.exists) {
        setPanMessage({ text: '❌ ' + (data.message || 'PAN already used'), type: 'error' });
      } else {
        setPanMessage({ text: '✓ PAN number is available', type: 'success' });
      }
    } catch (e) {
      console.error('PAN check failed', e);
      setPanMessage({ text: '', type: '' });
    }
  };

  // Bank Account Duplicate Check
  const handleBankAccountBlur = async () => {
    if (!formData.bankAccountNumber) { setBankAccountMessage({ text: '', type: '' }); return; }
    if (formData.bankAccountNumber.length < 9) {
      setBankAccountMessage({ text: '⚠️ Account number must be at least 9 digits', type: 'error' });
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/auth/check_kyc.php?type=bank&value=${formData.bankAccountNumber}`);
      const data = await response.json();
      if (data.exists) {
        setBankAccountMessage({ text: '❌ ' + (data.message || 'Bank account already used'), type: 'error' });
      } else {
        setBankAccountMessage({ text: '✓ Bank account number is available', type: 'success' });
      }
    } catch (e) {
      console.error('Bank check failed', e);
      setBankAccountMessage({ text: '', type: '' });
    }
  };

  const validateStep = () => {
    setError('');
    if (currentStep === 1) {
      if (!formData.sponsorId) { setError('Please enter a Sponsor ID'); return false; }
      if (sponsorMessage.type === 'error') { setError('Please provide a valid Sponsor ID'); return false; }
      if (sponsorIsFull && !extremePositions) { setError('Sponsor is full.'); return false; }
    }
    if (currentStep === 2) {
      if (!formData.fullName || !formData.email || !formData.mobile || !formData.dateOfBirth) {
        setError('Please fill in all required fields'); return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError('Invalid email address'); return false;
      }
      // PAN validation
      if (!formData.panNumber || formData.panNumber.length !== 10) {
        setError('Please enter a valid 10-character PAN Card Number'); return false;
      }
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
      if (!panRegex.test(formData.panNumber)) {
        setError('PAN Card format is invalid (e.g. ABCDE1234F)'); return false;
      }
      // Bank account validation
      if (!formData.bankAccountNumber || formData.bankAccountNumber.length < 9) {
        setError('Please enter a valid Bank Account Number (min 9 digits)'); return false;
      }
      // Check if PAN or bank account showed duplicate
      if (panMessage.type === 'error') {
        setError('PAN Card Number issue: ' + panMessage.text); return false;
      }
      if (bankAccountMessage.type === 'error') {
        setError('Bank Account issue: ' + bankAccountMessage.text); return false;
      }
      // Age validation
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      if (age < 18) { setError('You must be at least 18 years old'); return false; }
    }
    if (currentStep === 3) {
      if (!formData.state || !formData.city || !formData.pinCode) {
        setError('Please fill in all required address fields'); return false;
      }
    }
    if (currentStep === 4) {
      if (!formData.password || !formData.confirmPassword || !formData.transactionPassword) {
        setError('Please fill in all security fields'); return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match'); return false;
      }
      if (formData.transactionPassword.length !== 4) {
        setError('Transaction PIN must be 4 digits'); return false;
      }
      if (!formData.agreeTerms) {
        setError('Please accept the Terms & Conditions'); return false;
      }
    }
    return true;
  };

  const handleNext = () => { if (validateStep()) setCurrentStep(prev => prev + 1); };
  const handlePrevious = () => { setError(''); setCurrentStep(prev => prev - 1); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    setError('');

    try {
      // Using logic from Signup.js but with more data
      const userData = {
        fullName: formData.fullName,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        transactionPassword: formData.transactionPassword,
        dateOfBirth: formData.dateOfBirth,
        sponsorId: formData.sponsorId,
        placement: formData.placement,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        pinCode: formData.pinCode,
        fullAddress: formData.fullAddress,
        panNumber: formData.panNumber,
        accountNumber: formData.bankAccountNumber
      };

      const result = await signup(userData);
      if (result.success) {
        setRegistrationSuccess(result.data);
      } else {
        setError(result.message || 'Registration failed');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = formData.password.length >= 8 ? 'strong' : formData.password.length >= 6 ? 'medium' : 'weak';

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center py-10 px-4 relative overflow-hidden font-sans">
      {/* Background Decoration */}
      <div className="absolute top-0 -left-4 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 pointer-events-none"></div>
      <div className="absolute bottom-0 -right-4 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-2xl space-y-8">
        {/* Header */}
        <div className="absolute top-6 left-6 z-20">
          <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-blue-600 font-bold transition-colors text-sm bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl shadow-sm border border-white/50">
            <ChevronLeft size={18} /> Back to Home
          </Link>
        </div>

        <div className="text-center space-y-2 pt-12">
          <img src="/logo.png" alt="Logo" className="h-20 mx-auto mb-2" />
          <h1 className="text-2xl font-bold text-gray-900">Create Private Account</h1>
          <p className="text-gray-500 text-sm font-medium">Join our network and start your success journey today.</p>
        </div>

        {/* Stepper (Exactly like Register.js) */}
        <div className="mb-6 overflow-x-auto pb-2">
          <div className="flex items-center justify-between min-w-[500px]">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;

              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center flex-1">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${isCompleted ? 'bg-green-500 text-white' :
                      isActive ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg' :
                        'bg-gray-200 text-gray-400'
                      }`}>
                      {isCompleted ? <CheckCircle size={20} /> : <StepIcon size={18} />}
                    </div>
                    <p className={`mt-1.5 text-[10px] font-bold uppercase tracking-wider ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                      }`}>{step.title}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 rounded ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Error Box */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm flex items-center gap-3 font-medium animate-fadeIn">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {/* Form Body */}
        <div className="bg-white rounded-3xl shadow-xl shadow-blue-500/5 border border-gray-100 p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* STEP 1: Sponsor */}
            {currentStep === 1 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 pb-4 border-b border-gray-50">
                  <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl"><GitBranch size={22} /></div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Sponsor Details</h2>
                    <p className="text-xs text-gray-500">Pick your sponsor and placement branch</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Sponsor User ID <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input type="text" name="sponsorId" value={formData.sponsorId} onChange={(e) => { handleChange(e); }} onBlur={() => verifySponsor(formData.sponsorId)} className="w-full pl-10 pr-10 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 uppercase font-semibold text-sm" placeholder="e.g. NX123456" />
                      {sponsorLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" size={18} />}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Sponsor Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input type="text" readOnly value={formData.sponsorName} className="w-full pl-10 pr-4 py-3.5 border border-gray-100 rounded-2xl bg-gray-50 text-gray-500 text-sm font-medium cursor-not-allowed" placeholder="Auto-filled" />
                    </div>
                  </div>

                  {sponsorMessage.text && (
                    <div className={`rounded-2xl p-4 text-xs font-bold animate-fadeIn ${sponsorMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : sponsorMessage.type === 'error' ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`}>
                      {sponsorMessage.text}
                    </div>
                  )}

                  {extremePositions && extremePositions.left && extremePositions.right && (
                    <div className="space-y-3 animate-fadeIn">
                      <label className="block text-sm font-semibold text-gray-700">Placement Position <span className="text-red-500">*</span></label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {extremePositions.left.is_available ? (
                          <label className={`cursor-pointer rounded-2xl border-2 p-4 flex items-center justify-center gap-3 transition-all ${formData.placement === 'left' ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm' : 'border-gray-100'}`}>
                            <input type="radio" name="placement" value="left" checked={formData.placement === 'left'} onChange={handleChange} className="hidden" />
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.placement === 'left' ? 'border-blue-600' : 'border-gray-300'}`}>
                              {formData.placement === 'left' && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                            </div>
                            <span className="font-bold text-sm uppercase">⬅️ Left</span>
                          </label>
                        ) : (
                          <label className={`cursor-pointer rounded-2xl border-2 p-4 transition-all ${formData.placement === 'extreme_left' ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm' : 'border-gray-100'}`}>
                            <input type="radio" name="placement" value="extreme_left" checked={formData.placement === 'extreme_left'} onChange={handleChange} className="hidden" />
                            <div className="text-center">
                              <p className="font-bold text-sm uppercase">🌳 Extreme Left</p>
                              <p className="text-[10px] opacity-70">Under: {extremePositions.left.extreme_target?.name || '...'}</p>
                            </div>
                          </label>
                        )}

                        {extremePositions.right.is_available ? (
                          <label className={`cursor-pointer rounded-2xl border-2 p-4 flex items-center justify-center gap-3 transition-all ${formData.placement === 'right' ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-sm' : 'border-gray-100'}`}>
                            <input type="radio" name="placement" value="right" checked={formData.placement === 'right'} onChange={handleChange} className="hidden" />
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.placement === 'right' ? 'border-purple-600' : 'border-gray-300'}`}>
                              {formData.placement === 'right' && <div className="w-2 h-2 rounded-full bg-purple-600" />}
                            </div>
                            <span className="font-bold text-sm uppercase">➡️ Right</span>
                          </label>
                        ) : (
                          <label className={`cursor-pointer rounded-2xl border-2 p-4 transition-all ${formData.placement === 'extreme_right' ? 'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700 shadow-sm' : 'border-gray-100'}`}>
                            <input type="radio" name="placement" value="extreme_right" checked={formData.placement === 'extreme_right'} onChange={handleChange} className="hidden" />
                            <div className="text-center">
                              <p className="font-bold text-sm uppercase">🌳 Extreme Right</p>
                              <p className="text-[10px] opacity-70">Under: {extremePositions.right.extreme_target?.name || '...'}</p>
                            </div>
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 2: Personal */}
            {currentStep === 2 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 pb-4 border-b border-gray-50">
                  <div className="p-2.5 bg-green-50 text-green-600 rounded-xl"><User size={22} /></div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Personal Data</h2>
                    <p className="text-xs text-gray-500">How should we address you?</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className="w-full pl-10 pr-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold" placeholder="As per documents" required />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full pl-10 pr-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold" placeholder="email@example.com" required />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mobile Number <span className="text-red-500">*</span></label>
                    <div className="relative flex">
                      <div className="bg-gray-50 border border-gray-200 border-r-0 rounded-l-2xl px-3 flex items-center text-gray-500 font-bold text-sm">+91</div>
                      <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} onBlur={handleMobileBlur} className="w-full pl-3 pr-4 py-3.5 border border-gray-200 rounded-r-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold" placeholder="9876543210" required />
                    </div>
                    {mobileMessage.text && <p className={`text-[10px] mt-1 font-bold ${mobileMessage.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{mobileMessage.text}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Date of Birth <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                        min="1920-01-01"
                        className="w-full pl-10 pr-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Gender <span className="text-red-500">*</span></label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold"
                      required
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Marital Status <span className="text-red-500">*</span></label>
                    <select
                      name="maritalStatus"
                      value={formData.maritalStatus}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold"
                      required
                    >
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="widowed">Widowed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nominee Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="nomineeName"
                      value={formData.nomineeName}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold"
                      placeholder="Enter Nominee Name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nominee Relation <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="nomineeRelation"
                      value={formData.nomineeRelation}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold"
                      placeholder="e.g. Spouse, Father, Mother"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">PAN Card Number <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        name="panNumber"
                        value={formData.panNumber}
                        onChange={handleChange}
                        onBlur={handlePanBlur}
                        className={`w-full pl-10 pr-4 py-3.5 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold uppercase font-mono tracking-wider ${panMessage.type === 'error' ? 'border-red-400 bg-red-50/30' : panMessage.type === 'success' ? 'border-green-400 bg-green-50/30' : 'border-gray-200'}`}
                        placeholder="ABCDE1234F"
                        maxLength="10"
                        required
                      />
                    </div>
                    {panMessage.text ? <p className={`text-[10px] mt-1 font-bold ${panMessage.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{panMessage.text}</p> : <p className="text-[10px] text-gray-400 mt-1 font-medium">10-character alphanumeric PAN</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Bank Account Number <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Landmark className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        name="bankAccountNumber"
                        value={formData.bankAccountNumber}
                        onChange={handleChange}
                        onBlur={handleBankAccountBlur}
                        className={`w-full pl-10 pr-4 py-3.5 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold font-mono tracking-wider ${bankAccountMessage.type === 'error' ? 'border-red-400 bg-red-50/30' : bankAccountMessage.type === 'success' ? 'border-green-400 bg-green-50/30' : 'border-gray-200'}`}
                        placeholder="Enter Account Number"
                        maxLength="18"
                        required
                      />
                    </div>
                    {bankAccountMessage.text ? <p className={`text-[10px] mt-1 font-bold ${bankAccountMessage.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>{bankAccountMessage.text}</p> : <p className="text-[10px] text-gray-400 mt-1 font-medium">Your bank account number for payouts</p>}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Address */}
            {currentStep === 3 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 pb-4 border-b border-gray-50">
                  <div className="p-2.5 bg-purple-50 text-purple-600 rounded-xl"><MapPin size={22} /></div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Address Details</h2>
                    <p className="text-xs text-gray-500">Tell us where you stay</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Country</label>
                    <select value={selectedCountryCode} onChange={handleCountryChange} className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white text-sm font-semibold">
                      {countries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">State <span className="text-red-500">*</span></label>
                    <select value={selectedStateCode} onChange={handleStateChange} className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white text-sm font-semibold" required>
                      <option value="">Select State</option>
                      {states.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">City <span className="text-red-500">*</span></label>
                    <select name="city" value={formData.city} onChange={handleChange} className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white text-sm font-semibold" required>
                      <option value="">Select City</option>
                      {cities.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Pin Code <span className="text-red-500">*</span></label>
                    <input type="text" name="pinCode" value={formData.pinCode} onChange={handleChange} className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold" placeholder="6-digit" maxLength="6" required />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Address</label>
                    <textarea name="fullAddress" value={formData.fullAddress} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-medium" rows="2" placeholder="Street, block, etc." />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: Security */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 pb-4 border-b border-gray-50">
                  <div className="p-2.5 bg-red-50 text-red-600 rounded-xl"><Shield size={22} /></div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Security Setup</h2>
                    <p className="text-xs text-gray-500">Passwords for login and TPIN for money</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} className="w-full pl-10 pr-10 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold" placeholder="Min 6 characters" required />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {formData.password && (
                        <div className="h-1 bg-gray-100 rounded-full mt-2 overflow-hidden">
                          <div className={`h-full transition-all ${passwordStrength === 'strong' ? 'bg-green-500 w-full' : passwordStrength === 'medium' ? 'bg-yellow-500 w-2/3' : 'bg-red-500 w-1/3'}`} />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="w-full pl-10 pr-10 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm font-semibold" placeholder="Same password" required />
                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Transaction PIN (4-Digits) <span className="text-red-500">*</span></label>
                    <div className="relative max-w-[200px]">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input type="password" name="transactionPassword" value={formData.transactionPassword} onChange={handleChange} className="w-full pl-10 pr-4 py-3.5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-mono text-lg tracking-[0.4em]" placeholder="XXXX" maxLength="4" required />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1 font-medium">Needed for withdrawals and transfers</p>
                  </div>

                  {/* Summary Card */}
                  <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-6 text-white shadow-lg shadow-blue-600/20">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                      <CheckCircle size={20} className="text-blue-200" /> Confirm Your Information
                    </h3>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                      <div><p className="opacity-70 text-[10px] uppercase font-bold tracking-wider">Full Name</p><p className="font-semibold">{formData.fullName || '-'}</p></div>
                      <div><p className="opacity-70 text-[10px] uppercase font-bold tracking-wider">Email</p><p className="font-semibold truncate">{formData.email || '-'}</p></div>
                      <div><p className="opacity-70 text-[10px] uppercase font-bold tracking-wider">Mobile</p><p className="font-semibold">{formData.mobile ? `+91 ${formData.mobile}` : '-'}</p></div>
                      <div><p className="opacity-70 text-[10px] uppercase font-bold tracking-wider">City</p><p className="font-semibold">{formData.city || '-'}</p></div>
                      <div><p className="opacity-70 text-[10px] uppercase font-bold tracking-wider">PAN</p><p className="font-semibold font-mono">{formData.panNumber || '-'}</p></div>
                      <div><p className="opacity-70 text-[10px] uppercase font-bold tracking-wider">Bank A/C</p><p className="font-semibold font-mono">{formData.bankAccountNumber ? '****' + formData.bankAccountNumber.slice(-4) : '-'}</p></div>
                    </div>
                  </div>

                  <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 cursor-pointer transition-colors hover:bg-gray-100">
                    <input type="checkbox" name="agreeTerms" checked={formData.agreeTerms} onChange={(e) => { if (!formData.agreeTerms) { e.preventDefault(); setShowDeclarationModal(true); } else { setFormData(prev => ({ ...prev, agreeTerms: false })); } }} className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="text-xs text-gray-500 leading-relaxed">
                      I have read and agree to the{' '}
                      <button type="button" onClick={() => setShowDeclarationModal(true)} className="text-blue-600 font-bold hover:underline">Direct Seller Agreement</button>{' '}
                      as per Gazette of India,{' '}
                      <button type="button" onClick={() => setActiveModal('terms')} className="text-blue-600 font-bold hover:underline">Terms of Service</button> and{' '}
                      <button type="button" onClick={() => setActiveModal('privacy')} className="text-blue-600 font-bold hover:underline">Privacy Policy</button>.
                      I confirm that the information provided is accurate and belongs to me.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-100">
              <button type="button" onClick={handlePrevious} disabled={currentStep === 1} className="flex items-center gap-2 px-6 py-3 bg-gray-50 text-gray-500 rounded-2xl font-bold hover:bg-gray-100 transition-all disabled:opacity-30 disabled:cursor-not-allowed text-sm">
                <ChevronLeft size={18} /> Back
              </button>

              {currentStep < 4 ? (
                <button type="button" onClick={handleNext} className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-bold hover:shadow-xl hover:shadow-blue-600/20 transform hover:-translate-y-0.5 transition-all text-sm">
                  Next Step <ChevronRight size={18} />
                </button>
              ) : (
                <button type="submit" disabled={loading} className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold hover:shadow-xl hover:shadow-green-600/20 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 text-sm">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
                  {loading ? 'Creating Account...' : 'Complete Registration'}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className="text-gray-500 text-sm text-center font-medium">
          Already part of the family?{' '}
          <Link to="/login" className="text-blue-600 font-bold hover:underline underline-offset-4">Sign in here</Link>
        </p>
      </div >

      {/* Registration Success Modal */}
      {
        registrationSuccess && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-fadeIn" />
            <div className="relative bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl border border-gray-100 animate-scaleIn text-center space-y-6">
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-2">
                <CheckCircle size={48} />
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl font-black text-gray-900">Registration Successful!</h2>
                <p className="text-gray-500 font-medium">Welcome to the family. Your account has been created.</p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-200/50">
                  <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">User ID</span>
                  <span className="text-blue-600 font-black text-lg font-mono">{registrationSuccess.user_id}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-200/50">
                  <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">Full Name</span>
                  <span className="text-gray-800 font-bold">{registrationSuccess.full_name}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-400 text-xs font-bold uppercase tracking-wider">Email</span>
                  <span className="text-gray-600 font-semibold text-sm">{registrationSuccess.email}</span>
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 flex gap-3 text-left">
                <AlertCircle className="text-amber-500 shrink-0" size={20} />
                <p className="text-[11px] text-amber-700 font-bold leading-relaxed">
                  IMPORTANT: Please save your User ID. You will need it to login to your dashboard.
                </p>
              </div>

              <button
                onClick={() => navigate('/login')}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-black text-lg hover:shadow-xl hover:shadow-blue-600/20 transform hover:-translate-y-1 transition-all flex items-center justify-center gap-2"
              >
                Go to Login <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )
      }

      {/* Modals */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms of Service">
        <p>By registering for our MLM platform, you agree to follow all business conduct guidelines and commission structures.</p>
        <p className="text-xs text-gray-400">Rest of the legal text would go here...</p>
      </Modal>

      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy">
        <p>We value your privacy. Your data is encrypted and used solely for account management and commission payouts.</p>
        <p className="text-xs text-gray-400">Rest of the privacy policy text would go here...</p>
      </Modal>

      {/* Direct Seller Agreement Modal */}
      {showDeclarationModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowDeclarationModal(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-2xl shadow-2xl border border-gray-100 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Direct Seller Agreement</h3>
                <p className="text-xs text-gray-500 mt-0.5">As per Gazette of India</p>
              </div>
              <button onClick={() => setShowDeclarationModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors p-1">✕</button>
            </div>
            <div className="overflow-y-auto p-6 space-y-5 text-sm text-gray-700 leading-relaxed flex-1">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wider mb-1">Compliance with Consumer Protection (Direct Selling) Rules, 2021</p>
                <p className="text-xs text-blue-500">(As notified in the Gazette of India)</p>
                <p className="text-base font-black text-gray-900 mt-2">SWIFT2AI LIFECARE PRIVATE LIMITED</p>
              </div>
              <p>In accordance with the Consumer Protection Act, 2019 and the Consumer Protection (Direct Selling) Rules, 2021, as notified by the Government of India in the Gazette of India, <strong>SWIFT2AI LIFECARE PRIVATE LIMITED</strong> ("Company") affirms and declares its compliance with the statutory requirements governing direct selling entities in India.</p>

              <div className="space-y-4">
                <div><h4 className="font-bold text-gray-900 mb-1">1. Legal Status of Direct Selling Entity</h4><p>The Company is a body corporate incorporated under the Companies Act, 2013 and is engaged in direct selling of products in India in accordance with applicable laws.</p></div>

                <div><h4 className="font-bold text-gray-900 mb-1">2. Prohibition of Pyramid and Money Circulation Schemes</h4><p>The Company does not promote, operate, or participate in any pyramid, chain, or money circulation schemes, whether directly or indirectly. The Company's business model is strictly based on the sale of genuine products and services.</p></div>

                <div><h4 className="font-bold text-gray-900 mb-1">3. Product-Based Compensation</h4><p>Any incentives, commissions, or benefits payable to direct sellers are derived solely from the sale of products and not from recruitment or enrollment of persons.</p></div>

                <div><h4 className="font-bold text-gray-900 mb-1">4. Mandatory Policies and Disclosures</h4><p>The Company maintains and publishes the following policies in compliance with the Rules:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-0.5 text-gray-600"><li>Terms and Conditions</li><li>Shipping Policy</li><li>Return and Refund Policy</li><li>Cancellation Policy</li><li>Grievance Redressal Mechanism</li><li>Direct Seller Code of Conduct</li></ul></div>

                <div><h4 className="font-bold text-gray-900 mb-1">5. Prohibition of Misleading Representations</h4><p>The Company and its direct sellers are prohibited from making any false, misleading, or exaggerated representations relating to:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-0.5 text-gray-600"><li>Income or earning potential</li><li>Health, therapeutic, or medical benefits</li><li>Product quality or performance beyond substantiated claims</li></ul></div>

                <div><h4 className="font-bold text-gray-900 mb-1">6. Grievance Redressal Mechanism</h4><p>The Company has established a grievance redressal mechanism in accordance with Rule 4 of the Consumer Protection (Direct Selling) Rules, 2021, for addressing consumer and direct seller complaints in a timely manner.</p></div>

                <div><h4 className="font-bold text-gray-900 mb-1">7. Consumer Protection and Transparency</h4><p>The Company ensures that consumers are provided with:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-0.5 text-gray-600"><li>Complete and accurate product information</li><li>Proper invoices and receipts</li><li>Transparent pricing and policies</li><li>Fair and ethical business practices</li></ul></div>

                <div><h4 className="font-bold text-gray-900 mb-1">8. Direct Seller Obligations</h4><p>All direct sellers associated with the Company are required to comply with:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-0.5 text-gray-600"><li>The Consumer Protection (Direct Selling) Rules, 2021</li><li>The Company's internal policies and code of conduct</li><li>All applicable central and state laws and regulations</li></ul></div>

                <div><h4 className="font-bold text-gray-900 mb-1">9. Compliance Declaration</h4><p>The Company affirms that it conducts its direct selling operations in compliance with the Consumer Protection Act, 2019 and the Consumer Protection (Direct Selling) Rules, 2021, as notified in the Gazette of India, and undertakes to adhere to all statutory obligations imposed thereunder.</p></div>
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowDeclarationModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition-all text-sm"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setFormData(prev => ({ ...prev, agreeTerms: true }));
                  setShowDeclarationModal(false);
                }}
                className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg transition-all text-sm flex items-center justify-center gap-2"
              >
                <CheckCircle size={16} /> I Understand & Agree
              </button>
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
                @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                .animate-scaleIn { animation: scaleIn 0.2s ease-out forwards; }
                .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                /* Custom Date Input Icon Styling */
                input[type="date"]::-webkit-calendar-picker-indicator {
                  opacity: 0.5;
                  cursor: pointer;
                }
            `}} />
    </div >
  );
};

export default Signup;
